import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Icon from '@mdi/react';
import { mdiClose, mdiMagnify } from '@mdi/js';
import { reportTypes, defaultReport, advancedReports } from '../../utils/reportTypes';
import {
    activeSubAccountSelector,
    createReportShare,
    deleteReportShare,
    changeView,
    commitSubAccountMappings,
} from '../../features/subaccounts/subaccountSlice';
import SelectionDraftActions from './SelectionDraftActions';

const toReportMapping = (report) => ({
    type: report?.type ?? report?.value ?? '',
    name: report?.name ?? '',
});

const reportKey = (report) => `${report.type}::${report.name}`;

const uniqueReports = (reports) => {
    const reportsByKey = new Map();
    reports.forEach((report) => {
        const normalizedReport = toReportMapping(report);
        reportsByKey.set(reportKey(normalizedReport), normalizedReport);
    });
    return [...reportsByKey.values()];
};

const useStyles = makeStyles(() => ({
    dialogPaper: {
        borderRadius: 18,
        background: 'linear-gradient(135deg, rgba(248,250,252,0.98), rgba(226,232,240,0.95))',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
        overflow: 'hidden',
    },
    dialogTitle: {
        padding: '20px 24px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        color: '#f8fafc',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
    },
    titleText: {
        fontWeight: 700,
        letterSpacing: '0.02em',
    },
    subtitleText: {
        fontSize: '0.8rem',
        color: 'rgba(226, 232, 240, 0.8)',
    },
    searchField: {
        minWidth: 240,
        backgroundColor: 'rgba(248, 250, 252, 0.95)',
        borderRadius: 12,
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(148, 163, 184, 0.4)',
        },
        '& .MuiInputBase-input': {
            fontSize: '0.9rem',
        },
    },
    closeButton: {
        padding: 6,
        borderRadius: 10,
        border: '1px solid rgba(148, 163, 184, 0.4)',
        color: '#e2e8f0',
        backgroundColor: 'rgba(15, 23, 42, 0.35)',
        '&:hover': {
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
        },
    },
    content: {
        padding: 20,
        paddingTop: 36,
    },
    panel: {
        borderRadius: 16,
        border: '1px solid rgba(148, 163, 184, 0.35)',
        backgroundColor: '#ffffff',
        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        minHeight: 320,
    },
    panelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    panelTitle: {
        fontWeight: 600,
        color: '#0f172a',
    },
    selectAllButton: {
        textTransform: 'none',
        borderRadius: 10,
        fontWeight: 600,
        background: 'linear-gradient(135deg, #38bdf8, #0ea5e9)',
        color: '#ffffff',
        boxShadow: '0 10px 18px rgba(14, 165, 233, 0.2)',
        '&:hover': {
            background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
        },
    },
    listGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: 12,
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 12,
        border: '1px solid rgba(148, 163, 184, 0.35)',
        backgroundColor: '#ffffff',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
    },
    itemShared: {
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        borderColor: 'rgba(15, 23, 42, 0.7)',
        color: '#f8fafc',
    },
    itemText: {
        fontSize: '0.92rem',
        fontWeight: 600,
        color: 'inherit',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    emptyText: {
        color: '#64748b',
        fontSize: '0.85rem',
    },
}));

const SubAccountReports = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const activeSubAccount = useSelector(activeSubAccountSelector);

    const [reportList, setReportList] = useState([]);
    const [baseReportList, setBaseReportList] = useState([]);
    const [initializedSubuserId, setInitializedSubuserId] = useState(null);
    const [search, setSearch] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const subuserId = activeSubAccount?.subuser_id;

    useEffect(() => {
        if (!subuserId || !Array.isArray(activeSubAccount?.reports) || initializedSubuserId === subuserId) {
            return;
        }
        const selectedReports = activeSubAccount.reports.length > 0
            ? uniqueReports(activeSubAccount.reports)
            : uniqueReports(defaultReport);
        setBaseReportList(selectedReports);
        setReportList(selectedReports);
        setInitializedSubuserId(subuserId);
        setSaveError('');
    }, [activeSubAccount?.reports, initializedSubuserId, subuserId]);

    const handleToggle = (report, nextChecked) => {
        if (!subuserId || isSaving) {
            return;
        }
        const normalizedReport = toReportMapping(report);
        const selectedReportKey = reportKey(normalizedReport);
        const shouldShare = typeof nextChecked === 'boolean'
            ? nextChecked
            : !reportList.some((item) => reportKey(item) === selectedReportKey);
        if (shouldShare) {
            setReportList((prev) => uniqueReports([...prev, normalizedReport]));
        } else {
            setReportList((prev) => prev.filter((item) => reportKey(item) !== selectedReportKey));
        }
        setSaveError('');
    };

    const allReports = useMemo(() => uniqueReports(reportTypes), []);
    const allSelected = allReports.length > 0
        && allReports.every((report) => reportList.some((item) => reportKey(item) === reportKey(report)));

    const handleSelectAllToggle = () => {
        if (!subuserId || isSaving) {
            return;
        }
        if (allSelected) {
            setReportList([]);
        } else {
            setReportList(allReports);
        }
        setSaveError('');
    };

    const addedReports = useMemo(() => {
        const baseKeys = new Set(baseReportList.map(reportKey));
        return reportList.filter((report) => !baseKeys.has(reportKey(report)));
    }, [baseReportList, reportList]);

    const removedReports = useMemo(() => {
        const draftKeys = new Set(reportList.map(reportKey));
        return baseReportList.filter((report) => !draftKeys.has(reportKey(report)));
    }, [baseReportList, reportList]);

    const hasPendingChanges = addedReports.length > 0 || removedReports.length > 0;

    const handleDiscard = () => {
        if (isSaving) {
            return;
        }
        setReportList(baseReportList);
        setSaveError('');
    };

    const handleSubmit = async () => {
        if (!subuserId || !hasPendingChanges || isSaving) {
            return false;
        }

        let persistedReports = baseReportList;
        setIsSaving(true);
        setSaveError('');
        try {
            if (removedReports.length > 0) {
                await dispatch(deleteReportShare({ subuser_id: subuserId, reports: removedReports })).unwrap();
                const removedKeys = new Set(removedReports.map(reportKey));
                persistedReports = persistedReports.filter((report) => !removedKeys.has(reportKey(report)));
                dispatch(commitSubAccountMappings({ subuser_id: subuserId, reports: persistedReports }));
            }
            if (addedReports.length > 0) {
                await dispatch(createReportShare({ subuser_id: subuserId, createReports: addedReports })).unwrap();
            }
            dispatch(commitSubAccountMappings({ subuser_id: subuserId, reports: reportList }));
            setBaseReportList(reportList);
            return true;
        } catch (error) {
            setBaseReportList(persistedReports);
            setSaveError('Some changes may have been saved. Please retry to finish applying this selection.');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const visibleReports = useMemo(() => {
        if (!search) {
            return advancedReports;
        }
        return advancedReports.filter((report) =>
            report.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [search]);

    const handleClose = () => {
        if (isSaving) {
            return;
        }
        if (hasPendingChanges && !window.confirm('Discard unsaved report changes?')) {
            return;
        }
        dispatch(changeView('main'));
    };

    if (!activeSubAccount) {
        return null;
    }

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: classes.dialogPaper }}
        >
            <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.titleRow}>
                    <Box>
                        <Typography variant="h6" className={classes.titleText}>
                            Report Selection
                        </Typography>
                        <Typography className={classes.subtitleText}>
                            {activeSubAccount?.person_name || 'Sub Account'}
                        </Typography>
                    </Box>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <TextField
                            className={classes.searchField}
                            size="small"
                            variant="outlined"
                            placeholder="Search reports"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon path={mdiMagnify} size={0.8} color="#64748b" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton onClick={handleClose} className={classes.closeButton}>
                            <Icon path={mdiClose} size={0.8} color="currentColor" />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <Box className={classes.panel}>
                    <Box className={classes.panelHeader}>
                        <Typography className={classes.panelTitle}>
                            Reports Shared - {reportList.length}
                        </Typography>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Typography variant="caption" color="text.secondary">
                                {visibleReports.length} total
                            </Typography>
                            <Button
                                size="small"
                                onClick={handleSelectAllToggle}
                                variant="contained"
                                className={classes.selectAllButton}
                                disabled={isSaving}
                            >
                                {allSelected ? 'Clear All' : 'Share All'}
                            </Button>
                        </Box>
                    </Box>
                    <Divider />
                    <Box className={classes.listGrid}>
                        {visibleReports.length > 0 ? (
                            visibleReports.map((report) => {
                                const isShared = reportList.some(
                                    (item) => reportKey(item) === reportKey(toReportMapping(report))
                                );
                                return (
                                    <Box
                                        key={report.name}
                                        className={`${classes.item} ${isShared ? classes.itemShared : ''}`}
                                    >
                                        <Typography className={classes.itemText}>
                                            {report.name}
                                        </Typography>
                                        <Switch
                                            checked={isShared}
                                            onChange={(event, checked) => handleToggle(report, checked)}
                                            disabled={isSaving}
                                            sx={{
                                                '& .MuiSwitch-thumb': {
                                                    color: isShared ? '#f8fafc' : '#0ea5e9',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: isShared
                                                        ? 'rgba(248, 250, 252, 0.35)'
                                                        : 'rgba(148, 163, 184, 0.4)',
                                                },
                                                '& .Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: isShared ? '#94a3b8' : '#38bdf8',
                                                },
                                            }}
                                        />
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography className={classes.emptyText}>
                                No reports found.
                            </Typography>
                        )}
                    </Box>
                    <SelectionDraftActions
                        itemLabel="report"
                        additions={addedReports}
                        removals={removedReports}
                        isSaving={isSaving}
                        saveError={saveError}
                        onDiscard={handleDiscard}
                        onSubmit={handleSubmit}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default SubAccountReports;
